/** Seconds breakdown for one exercise: work, set rest, rest after. */
export function sumExerciseDurationParts(workout) {
  if (!workout) return { workSeconds: 0, setRestSeconds: 0, restAfterSeconds: 0 };
  const sets = (workout.sets || []).filter((s) => !s.is_deleted);
  let workSeconds = 0;
  let setRestSeconds = 0;
  sets.forEach((set) => {
    workSeconds += Number(set.time_seconds ?? set.target_time_seconds) || 0;
    setRestSeconds += Number(set.rest_time_seconds ?? set.target_rest_seconds) || 0;
  });
  return {
    workSeconds,
    setRestSeconds,
    restAfterSeconds: Number(workout.rest_time_after) || 0,
  };
}

/**
 * Estimated calories for one set: reps × base_calories_per_rep when reps are
 * logged, otherwise reps_per_minute × time_seconds (converted to minutes) ×
 * base_calories_per_rep for time-based sets (e.g. 0 reps, holds/timed moves).
 */
export function sumSetCalories(set, workout) {
  const perRep = Number(workout?.base_calories_per_rep) || 0;
  if (!perRep) return 0;

  const reps = Number(set.reps ?? set.target_reps) || 0;
  if (reps > 0) return reps * perRep;

  const repsPerMinute = Number(workout?.reps_per_minute) || 0;
  const timeSeconds = Number(set.time_seconds ?? set.target_time_seconds) || 0;
  if (!repsPerMinute || !timeSeconds) return 0;

  const minutes = timeSeconds / 60;
  const totalReps = repsPerMinute * minutes;
  return perRep * totalReps;
}

/** Estimated calories for one exercise, summed across its sets. */
export function sumExerciseCalories(workout) {
  if (!workout?.base_calories_per_rep) return 0;
  return (workout.sets || [])
    .filter((s) => !s.is_deleted)
    .reduce((sum, set) => sum + sumSetCalories(set, workout), 0);
}

/**
 * Format seconds for display, e.g. "45s", "12 min", "1h 5 min".
 * Returns "0s" when seconds <= 0 so summary cells stay filled.
 */
export function formatWorkoutDuration(totalSeconds, { emptyAsZero = false } = {}) {
  const seconds = Math.max(0, Math.round(Number(totalSeconds) || 0));
  if (seconds <= 0) return emptyAsZero ? '0s' : '';

  if (seconds < 60) return `${seconds}s`;

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remSeconds = seconds % 60;

  if (hours > 0) {
    if (minutes === 0) return `${hours}h`;
    return `${hours}h ${minutes} min`;
  }

  if (remSeconds === 0) return `${minutes} min`;
  return `${minutes} min ${remSeconds}s`;
}

/**
 * Total + work / set-rest / rest-after breakdown from a workout bundle.
 * `periodWorkoutsFromBundle` is injected to avoid a circular import with workoutApi.
 */
export function buildWorkoutDurationSummary(
  workoutBundle,
  periods,
  workoutGroupTypes,
  periodWorkoutsFromBundle
) {
  const empty = {
    totalSeconds: 0,
    totalLabel: '',
    workSeconds: 0,
    setRestSeconds: 0,
    restAfterSeconds: 0,
    totalCalories: 0,
  };

  if (!workoutBundle || !periodWorkoutsFromBundle) return empty;

  let workSeconds = 0;
  let setRestSeconds = 0;
  let restAfterSeconds = 0;
  let totalCalories = 0;

  (periods || []).forEach((period) => {
    const { workouts } = periodWorkoutsFromBundle(
      workoutBundle,
      workoutGroupTypes,
      period.key
    );
    (workouts || []).forEach((w) => {
      const parts = sumExerciseDurationParts(w);
      workSeconds += parts.workSeconds;
      setRestSeconds += parts.setRestSeconds;
      restAfterSeconds += parts.restAfterSeconds;
      totalCalories += sumExerciseCalories(w);
    });
  });

  const totalSeconds = workSeconds + setRestSeconds + restAfterSeconds;
  return {
    totalSeconds,
    totalLabel: formatWorkoutDuration(totalSeconds),
    workSeconds,
    setRestSeconds,
    restAfterSeconds,
    totalCalories,
  };
}
